import React, { useMemo } from "react";

/**
 * Top-down bazaar bowl — an overhead view of a warm clay bowl holding a
 * heap of coloured spice powder, as if you're looking down at a market
 * stall or a mise-en-place tray.
 *
 * The mound is drawn as an SVG bezier "dome" so it reads as a proper heap
 * of powder — round, three-dimensional, with side-lit highlight and a
 * shaded right slope. When brimming the peak of the heap visibly rises
 * above the bowl's rim.
 *
 * Two ambient effects:
 *   • `puff`    — set true right after a refill; the mound briefly springs
 *                 up ~20% (750ms cubic-bezier). Feels like a spoonful of
 *                 fresh powder just landed.
 *   • `sparkle` — periodic (from parent) 900ms glimmer on the peak of the
 *                 heap. Tells the user the drawer is a living surface.
 */
export default function SpiceBowl({
  name,
  fill = 1,
  color = "#b58a4a",
  outOfStock = false,
  size = 140,
  puff = false,
  sparkle = false,
  onClick,
  "data-testid": testId,
}) {
  // Small deterministic tilt/offset per name so a row of bowls feels
  // arranged, not gridded.
  const jitter = useMemo(() => {
    let h = 0;
    for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) | 0;
    const rot = ((h % 100) / 100 - 0.5) * 3;         // ±1.5°
    const dx  = ((h >> 8) % 100) / 100 * 3 - 1.5;     // ±1.5px
    // A subtle sparkle position offset — off-centre so the glimmer feels
    // organic instead of always landing on the exact middle of the heap.
    const sx = ((h >> 12) % 100) / 100 * 8 - 4;       // ±4px along the heap
    const sy = ((h >> 20) % 100) / 100 * 3;           // 0..3px down from peak
    return { rot, dx, sx, sy };
  }, [name]);

  const clampedFill = outOfStock ? 0 : Math.max(0, Math.min(1, fill));
  const low = fill < 0.25 || outOfStock;

  // ─── Bowl geometry (100×70 viewBox) ──────────────────────────────────
  const W = 100, H = 70;
  const cx = 50, cy = 42;
  const rimRx = 44, rimRy = 14;         // outer rim
  const innerRx = 40, innerRy = 12;     // inner shadowed ring
  const bottomRx = 32, bottomRy = 9;    // where the bowl "floor" starts

  // ─── Heap geometry (SVG bezier dome) ─────────────────────────────────
  // baseY sits *inside* the bowl (just above the floor). heapH is how
  // tall the heap rises above baseY. At full fill the peak sits ABOVE
  // the bowl's rim, which is what makes the heap read as three-dimensional.
  const moundBaseY = cy - 2;                        // just inside the rim line
  const moundRx    = 12 + 28 * clampedFill;         // widens with fill
  const heapH      = 5 + 22 * clampedFill;          // grows tall with fill
  const peakY      = moundBaseY - heapH;

  // Bezier dome path: two quadratic curves from the left base through
  // the peak to the right base. Control points pull the sides inward
  // slightly to create a rounded pear/hip contour instead of a triangle.
  const domePath = [
    `M ${cx - moundRx} ${moundBaseY}`,
    `Q ${cx - moundRx * 0.65} ${peakY + heapH * 0.25}, ${cx} ${peakY}`,
    `Q ${cx + moundRx * 0.65} ${peakY + heapH * 0.25}, ${cx + moundRx} ${moundBaseY}`,
    `Z`,
  ].join(" ");

  // Left "cheek" highlight — a soft crescent on the top-left slope where
  // an imagined overhead light source hits the heap. Also drawn as a
  // bezier so it curves naturally with the dome.
  const highlightPath = [
    `M ${cx - moundRx * 0.85} ${moundBaseY - heapH * 0.1}`,
    `Q ${cx - moundRx * 0.55} ${peakY + heapH * 0.05}, ${cx - moundRx * 0.05} ${peakY + heapH * 0.15}`,
    `Q ${cx - moundRx * 0.35} ${peakY + heapH * 0.5}, ${cx - moundRx * 0.85} ${moundBaseY - heapH * 0.1}`,
    `Z`,
  ].join(" ");

  // Simple manual shading: raise / lower each RGB channel to get
  // shade tones from the base spice colour.
  const shade = (hex, delta) => {
    if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;
    const r = Math.max(0, Math.min(255, parseInt(hex.slice(1, 3), 16) + delta));
    const g = Math.max(0, Math.min(255, parseInt(hex.slice(3, 5), 16) + delta));
    const b = Math.max(0, Math.min(255, parseInt(hex.slice(5, 7), 16) + delta));
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
  };
  const powderDark = shade(color, -55);
  const powderHot  = shade(color, +45);
  const powderMid  = shade(color, +5);

  const amount = outOfStock
    ? "out of stock"
    : fill < 0.15 ? "almost gone"
    : fill < 0.40 ? "running low"
    : fill < 0.75 ? "half full"
    : "brimming";

  const uid = `bowl-${name.replace(/\W+/g, "-").toLowerCase()}`;

  const displayName = name.length > 18 ? name.slice(0, 17).trimEnd() + "…" : name;
  const nameSize = displayName.length > 15 ? 12 : displayName.length > 12 ? 13 : 14;

  // Sparkle position: near the top-left of the peak (where the light
  // would catch it). Deterministic per bowl via `jitter.sx`/`sy`.
  const sparkleX = cx - moundRx * 0.15 + jitter.sx;
  const sparkleY = peakY + jitter.sy + 1;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group relative flex flex-col items-center gap-1.5 focus:outline-none rounded-xl transition-transform"
      style={{
        width: size,
        transform: `translate(${jitter.dx}px, 0) rotate(${jitter.rot}deg)`,
        opacity: outOfStock ? 0.55 : 1,
      }}
      title={outOfStock ? `${name} — out of stock` : `${name} — ${amount}`}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={size}
        height={size * (H / W)}
        className="transition-transform group-hover:-translate-y-1 overflow-visible"
        aria-hidden="true"
      >
        <defs>
          {/* Bowl clay — warm terracotta rim into deeper red */}
          <radialGradient id={`${uid}-clay`} cx="50%" cy="35%" r="65%">
            <stop offset="0%"   stopColor="#c9885a"/>
            <stop offset="55%"  stopColor="#8a4c2a"/>
            <stop offset="100%" stopColor="#4a2812"/>
          </radialGradient>
          {/* Bowl interior — darker shadow ring */}
          <radialGradient id={`${uid}-shadow`} cx="50%" cy="50%" r="60%">
            <stop offset="0%"   stopColor="#3a1e0c"/>
            <stop offset="100%" stopColor="#1a0d05"/>
          </radialGradient>
          {/* Heap fill — dark base → mid → hot crown at the peak.
              Vertical linear gradient so the top of the heap catches light
              and the bottom stays deep in shadow. */}
          <linearGradient id={`${uid}-heap`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={powderHot}/>
            <stop offset="35%"  stopColor={color}/>
            <stop offset="70%"  stopColor={powderMid}/>
            <stop offset="100%" stopColor={powderDark}/>
          </linearGradient>
          {/* Grain speckle on the heap */}
          <pattern id={`${uid}-grain`} width="2" height="2" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.35" fill="#000" opacity="0.22"/>
            <circle cx="0.5" cy="1.5" r="0.2" fill="#fff" opacity="0.14"/>
          </pattern>
          {/* Drop shadow under the bowl */}
          <radialGradient id={`${uid}-drop`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#000" stopOpacity="0.7"/>
            <stop offset="100%" stopColor="#000" stopOpacity="0"/>
          </radialGradient>
          {/* Sparkle glow — small radial for the star shimmer */}
          <radialGradient id={`${uid}-sparkle`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#fff8ec" stopOpacity="1"/>
            <stop offset="45%"  stopColor="#ffe8b8" stopOpacity="0.7"/>
            <stop offset="100%" stopColor="#ffd68a" stopOpacity="0"/>
          </radialGradient>
        </defs>

        {/* 1 — soft floor shadow underneath the bowl */}
        <ellipse cx={cx} cy={cy + rimRy + 3} rx={rimRx * 0.95} ry="5"
                 fill={`url(#${uid}-drop)`}/>

        {/* 2 — outer bowl rim (clay body) */}
        <ellipse cx={cx} cy={cy} rx={rimRx} ry={rimRy}
                 fill={`url(#${uid}-clay)`}/>

        {/* 3 — inner rim highlight (thin lighter ring) */}
        <ellipse cx={cx} cy={cy - 1} rx={rimRx - 2} ry={rimRy - 1}
                 fill="none" stroke="#e8b98a" strokeOpacity="0.35" strokeWidth="0.5"/>

        {/* 4 — bowl interior shadow (the cavity) */}
        <ellipse cx={cx} cy={cy} rx={innerRx} ry={innerRy}
                 fill={`url(#${uid}-shadow)`}/>

        {/* 5 — bowl floor / bottom (deepest point) */}
        <ellipse cx={cx} cy={cy} rx={bottomRx} ry={bottomRy}
                 fill="#170a03"/>

        {/* 6 — the heap of powder (bezier dome) */}
        {clampedFill > 0.03 && (
          <g
            className={puff ? "spice-bowl-puff" : undefined}
            style={{
              transformOrigin: `${cx}px ${moundBaseY}px`,
              transformBox: "fill-box",
            }}
          >
            {/* Dark under-ring — powder settled at the base */}
            <ellipse
              cx={cx} cy={moundBaseY}
              rx={moundRx} ry={heapH * 0.2 + 2}
              fill={powderDark}
              opacity={0.9}
            />
            {/* Main heap — the dome shape */}
            <path d={domePath} fill={`url(#${uid}-heap)`}/>
            {/* Grain texture layered over the heap */}
            <path d={domePath} fill={`url(#${uid}-grain)`} opacity={0.6}/>
            {/* Soft left-cheek highlight (light source top-left) */}
            {clampedFill > 0.25 && (
              <path
                d={highlightPath}
                fill={powderHot}
                opacity={0.42}
              />
            )}
            {/* Bright specular dot near the peak — only for well-stocked heaps */}
            {clampedFill > 0.45 && (
              <ellipse
                cx={cx - moundRx * 0.12} cy={peakY + heapH * 0.15}
                rx={moundRx * 0.14} ry={heapH * 0.09}
                fill="#fff8ec"
                opacity="0.35"
              />
            )}
          </g>
        )}

        {/* 7 — empty-bowl dashed inner ring (out of stock only) */}
        {outOfStock && (
          <ellipse cx={cx} cy={cy} rx={bottomRx - 2} ry={bottomRy - 1}
                   fill="none"
                   stroke="#b45a37" strokeOpacity="0.6" strokeWidth="0.5"
                   strokeDasharray="1.5 2"/>
        )}

        {/* 8 — sparkle. A radial glow + a 4-point star cross. Rendered
             only while `sparkle` is true. Both elements animate together
             via .spice-bowl-sparkle keyframes (scale + opacity). */}
        {sparkle && clampedFill > 0.15 && !outOfStock && (
          <g
            className="spice-bowl-sparkle"
            style={{
              transformOrigin: `${sparkleX}px ${sparkleY}px`,
              transformBox: "fill-box",
            }}
          >
            <circle
              cx={sparkleX} cy={sparkleY} r="5"
              fill={`url(#${uid}-sparkle)`}
            />
            {/* Star cross — vertical + horizontal spike */}
            <path
              d={`M ${sparkleX} ${sparkleY - 4} L ${sparkleX} ${sparkleY + 4}
                  M ${sparkleX - 4} ${sparkleY} L ${sparkleX + 4} ${sparkleY}`}
              stroke="#fff8ec" strokeWidth="0.7" strokeLinecap="round"
              opacity="0.9"
            />
          </g>
        )}
      </svg>

      {/* Caption — italic serif name + soft amount subtitle on the wood */}
      <div className="text-center leading-tight -mt-0.5">
        <div
          style={{
            fontFamily: "'Fraunces','Playfair Display',serif",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: nameSize,
            color: outOfStock ? "#e08a5c" : low ? "#f0b494" : "#f5e6c8",
            textShadow: "0 1px 2px rgba(0,0,0,0.55)",
            letterSpacing: "0.01em",
            lineHeight: 1.15,
          }}
        >
          {displayName}
        </div>
        <div
          style={{
            fontFamily: "'Fraunces','Playfair Display',serif",
            fontStyle: "italic",
            fontSize: 9.5,
            color: outOfStock || fill < 0.25 ? "#e08a5c" : "#c9a575",
            letterSpacing: "0.06em",
            marginTop: 1,
          }}
        >
          {amount}
        </div>
      </div>
    </button>
  );
}
